import { BlockTool, BlockToolData } from '@editorjs/editorjs'
import { uploaderService } from '../../../shared/service'
import { resolvePathFile } from '../../../../core/until'

export interface ImageTextData extends BlockToolData {
  imageUrl: string
  imageAlt: string
  textContent: string
  textPosition: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'
  textColor: string
  backgroundColor: string
  textSize: 'small' | 'medium' | 'large' | 'xlarge'
  overlayOpacity: number
  height: number
}

export class ImageTextTool implements BlockTool {
  private data: ImageTextData
  private wrapper: HTMLElement | null = null
  private config: any

  static get toolbox() {
    return {
      title: 'Image with Text',
      icon: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="3" width="16" height="12" rx="2" stroke="currentColor" stroke-width="1.5" fill="none"/><path d="M6 8l2 2 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><text x="10" y="16" text-anchor="middle" font-size="8" fill="currentColor">T</text></svg>'
    }
  }

  static get isReadOnlySupported() {
    return true
  }

  constructor({ data, config }: { data?: ImageTextData; config?: any }) {
    this.data = {
      imageUrl: data?.imageUrl || '',
      imageAlt: data?.imageAlt || '',
      textContent: data?.textContent || '',
      textPosition: data?.textPosition || 'center',
      textColor: data?.textColor || '#ffffff',
      backgroundColor: data?.backgroundColor || 'rgba(0, 0, 0, 0.3)',
      textSize: data?.textSize || 'medium',
      height: data?.height || 300,
      overlayOpacity: data?.overlayOpacity || 0.5,
      ...data
    }
    this.config = config
  }

  render() {
    this.wrapper = document.createElement('div')
    this.wrapper.classList.add('image-text-tool')
    this.wrapper.style.cssText = `
      border: 2px dashed #ddd;
      border-radius: 8px;
      padding: 20px;
      margin: 10px 0;
      min-height: ${this.data.height}px;
      position: relative;
      background: #f9f9f9;
    `

    this.createEditor()
    return this.wrapper
  }

  private createEditor() {
    if (!this.wrapper) return

    // Create the image container
    const imageContainer = document.createElement('div')
    imageContainer.style.cssText = `
      position: relative;
      width: 100%;
      height: ${this.data.height}px;
      border-radius: 8px;
      overflow: hidden;
      background: #f0f0f0;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 15px;
    `

    // Create the image element
    const image = document.createElement('img')
    image.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: ${this.data.imageUrl ? 'block' : 'none'};
    `
    image.src = this.data.imageUrl
    image.alt = this.data.imageAlt

    // Create overlay
    const overlay = document.createElement('div')
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${this.data.backgroundColor};
      opacity: ${this.data.overlayOpacity};
      display: ${this.data.imageUrl ? 'block' : 'none'};
    `

    // Create text element
    const textElement = document.createElement('div')
    textElement.style.cssText = `
      position: absolute;
      color: ${this.data.textColor};
      font-size: ${this.getTextSize()};
      font-weight: 600;
      padding: 20px;
      z-index: 2;
      max-width: calc(100% - 40px);
      word-wrap: break-word;
      box-sizing: border-box;
      ${this.getTextPosition()}
    `
    textElement.textContent = this.data.textContent || 'Text Placeholder'

    // Create placeholder when no image
    const placeholder = document.createElement('div')
    placeholder.style.cssText = `
      display: ${this.data.imageUrl ? 'none' : 'flex'};
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: #999;
      font-size: 16px;
    `
    placeholder.innerHTML = `
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21,15 16,10 5,21"/>
      </svg>
      <span style="margin-top: 10px;">Click to upload image</span>
    `

    // Add click handlers
    imageContainer.addEventListener('click', () => this.openImageUpload())

    // Assemble the image container
    imageContainer.appendChild(image)
    imageContainer.appendChild(overlay)
    imageContainer.appendChild(textElement)
    imageContainer.appendChild(placeholder)

    // Create controls
    const controls = this.createControls()

    this.wrapper.appendChild(imageContainer)
    this.wrapper.appendChild(controls)
  }

  private createControls() {
    const controls = document.createElement('div')
    controls.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 10px;
    `


    // Text textarea
    const textInput = document.createElement('textarea')
    textInput.placeholder = 'Text content'
    textInput.value = this.data.textContent
    textInput.rows = 3
    textInput.style.cssText = `
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      resize: vertical;
      font-family: inherit;
      box-sizing: border-box;
    `
    textInput.addEventListener('input', (e) => {
      this.data.textContent = (e.target as HTMLTextAreaElement).value
      this.updatePreview()
    })

    // Text position select
    const positionSelect = document.createElement('select')
    positionSelect.style.cssText = `
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
    `
    const positions = [
      { value: 'top-left', label: 'Top Left' },
      { value: 'top-center', label: 'Top Center' },
      { value: 'top-right', label: 'Top Right' },
      { value: 'center-left', label: 'Center Left' },
      { value: 'center', label: 'Center' },
      { value: 'center-right', label: 'Center Right' },
      { value: 'bottom-left', label: 'Bottom Left' },
      { value: 'bottom-center', label: 'Bottom Center' },
      { value: 'bottom-right', label: 'Bottom Right' }
    ]
    positions.forEach(pos => {
      const option = document.createElement('option')
      option.value = pos.value
      option.textContent = pos.label
      option.selected = pos.value === this.data.textPosition
      positionSelect.appendChild(option)
    })
    positionSelect.addEventListener('change', (e) => {
      this.data.textPosition = (e.target as HTMLSelectElement).value as any
      this.updatePreview()
    })

    // Text color input
    const colorInput = document.createElement('input')
    colorInput.type = 'color'
    colorInput.value = this.data.textColor
    colorInput.style.cssText = `
      width: 40px;
      height: 36px;
      border: 1px solid #ddd;
      border-radius: 4px;
      cursor: pointer;
    `
    colorInput.addEventListener('change', (e) => {
      this.data.textColor = (e.target as HTMLInputElement).value
      this.updatePreview()
    })

    // Height input
    const heightInput = document.createElement('input')
    heightInput.type = 'number'
    heightInput.placeholder = 'Height (px)'
    heightInput.value = this.data.height.toString()
    heightInput.min = '100'
    heightInput.max = '800'
    heightInput.style.cssText = `
      width: 120px;
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
    `
    heightInput.addEventListener('input', (e) => {
      this.data.height = parseInt((e.target as HTMLInputElement).value) || 300
      this.updatePreview()
    })

    // Create a row for the other controls
    const controlsRow = document.createElement('div')
    controlsRow.style.cssText = `
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      align-items: center;
    `

    controlsRow.appendChild(positionSelect)
    controlsRow.appendChild(colorInput)
    controlsRow.appendChild(heightInput)

    controls.appendChild(textInput)
    controls.appendChild(controlsRow)

    return controls
  }

  private openImageUpload() {
    // Create a file input element
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = 'image/*'
    fileInput.style.display = 'none'
    
    fileInput.addEventListener('change', async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        try {
          // Show loading state
          this.showLoadingState()
          
          // Upload the image
          const [result] = await uploaderService.uploadImages([file as any])
          const imageUrl = resolvePathFile(result.filename)
          
          // Update the data
          this.data.imageUrl = imageUrl
          this.data.imageAlt = file.name
          this.updatePreview()
          
          // Hide loading state
          this.hideLoadingState()
        } catch (error) {
          console.error('Error uploading image:', error)
          alert('Failed to upload image. Please try again.')
          this.hideLoadingState()
        }
      }
    })
    
    // Add to DOM, trigger click, then remove
    document.body.appendChild(fileInput)
    fileInput.click()
    document.body.removeChild(fileInput)
  }

  private showLoadingState() {
    if (!this.wrapper) return
    
    const imageContainer = this.wrapper.querySelector('div') as HTMLElement
    if (!imageContainer) return
    
    const loadingOverlay = document.createElement('div')
    loadingOverlay.id = 'image-loading-overlay'
    loadingOverlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      border-radius: 8px;
    `
    loadingOverlay.innerHTML = `
      <div style="text-align: center; color: #666;">
        <div style="width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 10px;"></div>
        <div>Uploading image...</div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `
    
    imageContainer.appendChild(loadingOverlay)
  }

  private hideLoadingState() {
    if (!this.wrapper) return
    
    const loadingOverlay = this.wrapper.querySelector('#image-loading-overlay')
    if (loadingOverlay) {
      loadingOverlay.remove()
    }
  }


  private updatePreview() {
    if (!this.wrapper) return

    const imageContainer = this.wrapper.querySelector('div') as HTMLElement
    if (!imageContainer) return

    const image = imageContainer.querySelector('img') as HTMLImageElement
    const overlay = imageContainer.querySelector('div[style*="position: absolute"]') as HTMLElement
    const textElement = imageContainer.querySelector('div[style*="color:"]') as HTMLElement
    const placeholder = imageContainer.querySelector('div[style*="display: flex"]') as HTMLElement

    if (image) {
      image.src = this.data.imageUrl
      image.style.display = this.data.imageUrl ? 'block' : 'none'
    }

    if (overlay) {
      overlay.style.background = this.data.backgroundColor
      overlay.style.opacity = this.data.overlayOpacity.toString()
      overlay.style.display = this.data.imageUrl ? 'block' : 'none'
    }

    if (textElement) {
      textElement.textContent = this.data.textContent || 'Text Placeholder'
      textElement.style.color = this.data.textColor
      textElement.style.fontSize = this.getTextSize()
      
      // Reset positioning styles to avoid conflicts
      textElement.style.top = ''
      textElement.style.left = ''
      textElement.style.right = ''
      textElement.style.bottom = ''
      textElement.style.transform = ''
      textElement.style.textAlign = ''
      
      // Apply new positioning styles
      const positionStyles = this.getTextPosition()
      const stylePairs = positionStyles.split(';').filter(pair => pair.trim())
      stylePairs.forEach(pair => {
        const [property, value] = pair.split(':').map(s => s.trim())
        if (property && value) {
          textElement.style[property as any] = value
        }
      })
    }

    if (placeholder) {
      placeholder.style.display = this.data.imageUrl ? 'none' : 'flex'
    }

    imageContainer.style.height = `${this.data.height}px`
  }

  private getTextSize(): string {
    const sizes = {
      small: '14px',
      medium: '18px',
      large: '24px',
      xlarge: '32px'
    }
    return sizes[this.data.textSize] || sizes.medium
  }

  private getTextPosition(): string {
    const positions = {
      'top-left': 'top: 0; left: 0; text-align: left;',
      'top-center': 'top: 0; left: 50%; transform: translateX(-50%); text-align: center;',
      'top-right': 'top: 0; right: 0; text-align: right;',
      'center-left': 'top: 50%; left: 0; transform: translateY(-50%); text-align: left;',
      'center': 'top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center;',
      'center-right': 'top: 50%; right: 0; transform: translateY(-50%); text-align: right;',
      'bottom-left': 'bottom: 0; left: 0; text-align: left;',
      'bottom-center': 'bottom: 0; left: 50%; transform: translateX(-50%); text-align: center;',
      'bottom-right': 'bottom: 0; right: 0; text-align: right;'
    }
    return positions[this.data.textPosition] || positions.center
  }

  save(): ImageTextData {
    return {
      ...this.data
    }
  }

  static get sanitize() {
    return {
      imageUrl: {},
      imageAlt: {},
      textContent: {},
      textPosition: {},
      textColor: {},
      backgroundColor: {},
      textSize: {},
      height: {},
      overlayOpacity: {}
    }
  }
}
